import React, { useState, useEffect } from "react";
import {
  Button,
  Input,
  Tag,
  Modal,
  Space,
  Typography,
  Tooltip,
  Select,
  DatePicker,
  Card,
  Row,
  Col,
  Statistic,
} from "antd";
import {
  DollarOutlined,
  TeamOutlined,
  RiseOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import Table from "../../../core/common/dataTable/index";
import dayjs from "dayjs";
import api from "../../../core/axios/axiosInstance";
import { useDebounce } from "../../../core/hooks/useDebounce";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const PLAN_STATUS_COLORS = {
  active: "success",
  trial: "processing",
  cancelled: "error",
  past_due: "warning",
};

const INVOICE_STATUS_COLORS = {
  paid: "success",
  open: "processing",
  void: "default",
  uncollectible: "error",
  draft: "default",
};

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "trial", label: "Trial" },
  { value: "past_due", label: "Past Due" },
  { value: "cancelled", label: "Cancelled" },
];

function centsToDisplay(cents) {
  if (cents == null) return "$0.00";
  return "$" + (cents / 100).toFixed(2);
}

export default function AdminBilling() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState(null);

  const search = useDebounce(searchInput, 350);

  const [invoiceModal, setInvoiceModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesTotal, setInvoicesTotal] = useState(0);
  const [invoicePage, setInvoicePage] = useState(1);
  const invoicePageSize = 10;

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchCompanies = async (p = page, ps = pageSize, s = search, sf = statusFilter, dr = dateRange) => {
    setLoading(true);
    try {
      const res = await api.post("/superAdmin/billing", {
        page: p,
        limit: ps,
        search: s,
        statusFilter: sf === "all" ? "" : sf,
        dateFrom: dr?.[0] ? dr[0].startOf("day").toISOString() : null,
        dateTo: dr?.[1] ? dr[1].endOf("day").toISOString() : null,
      });
      setCompanies(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch {
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const res = await api.get("/superAdmin/billing/stats");
      setStats(res.data);
    } catch {
      // silent
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchCompanies(page, pageSize, search, statusFilter, dateRange);
  }, [page, pageSize, search, statusFilter, dateRange]);

  const fetchInvoices = async (company, p = 1) => {
    setInvoicesLoading(true);
    try {
      const res = await api.post("/superAdmin/billing/invoices", {
        userId: company._id,
        page: p,
        limit: invoicePageSize,
      });
      setInvoices(res.data?.invoices || []);
      setInvoicesTotal(res.data?.total || 0);
    } catch {
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const openInvoiceModal = (record) => {
    setSelectedCompany(record);
    setInvoicePage(1);
    setInvoices([]);
    setInvoiceModal(true);
    fetchInvoices(record, 1);
  };

  const handleInvoicePageChange = (p) => {
    setInvoicePage(p);
    fetchInvoices(selectedCompany, p);
  };

  const mainColumns = [
    {
      title: "Company Name",
      dataIndex: "companyName",
      render: (val, record) => (
        <span style={{ fontWeight: 600 }}>{val || record.name || "—"}</span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (val) => val || "—",
    },
    {
      title: "Plan Status",
      dataIndex: "planStatus",
      render: (status) => (
        <Tag color={PLAN_STATUS_COLORS[status] || "default"}>
          {status || "—"}
        </Tag>
      ),
    },
    {
      title: "Total Paid",
      dataIndex: "totalPaid",
      render: (val) => centsToDisplay(val),
    },
    {
      title: "Invoices",
      dataIndex: "invoiceCount",
      render: (val) => <Tag>{val ?? 0}</Tag>,
    },
    {
      title: "Last Invoice",
      dataIndex: "lastInvoiceDate",
      render: (val) => (val ? dayjs(val).format("MMM D, YYYY") : "—"),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openInvoiceModal(record)}>
            View Invoices
          </Button>
        </Space>
      ),
    },
  ];

  const invoiceColumns = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
      render: (val) => val || "—",
    },
    {
      title: "Type",
      render: (_, record) =>
        record.invoiceType || record.planId?.name || "—",
    },
    {
      title: "Amount Paid",
      dataIndex: "amountPaid",
      render: (val, r) => {
        const displayCents = (val != null && val > 0) ? val : (r.seatFaceAmount ?? r.amount ?? r.planAmount ?? 0);
        return centsToDisplay(displayCents);
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={INVOICE_STATUS_COLORS[status] || "default"}>
          {status || "—"}
        </Tag>
      ),
    },
    {
      title: "Period",
      render: (_, record) => {
        const start = record.billingPeriodStart
          ? dayjs(record.billingPeriodStart).format("MMM D, YYYY")
          : null;
        const end = record.billingPeriodEnd
          ? dayjs(record.billingPeriodEnd).format("MMM D, YYYY")
          : null;
        if (!start && !end) return "—";
        return `${start || "?"} – ${end || "?"}`;
      },
    },
    {
      title: "Date",
      dataIndex: "stripeCreatedAt",
      render: (val) => (val ? dayjs(val).format("MMM D, YYYY") : "—"),
    },
    {
      title: "Download",
      dataIndex: "invoicePdf",
      render: (url) =>
        url ? (
          <Tooltip title="Download PDF">
            <Button
              type="link"
              size="small"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              PDF
            </Button>
          </Tooltip>
        ) : (
          <Text type="secondary">N/A</Text>
        ),
    },
  ];

  const modalTitle = selectedCompany
    ? `Invoices — ${selectedCompany.companyName || selectedCompany.name || selectedCompany.email}`
    : "Invoices";

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="card mt-3">
          <div className="card-body pb-2">
            <h4 className="page-title mb-0">Billing Management</h4>
          </div>
        </div>

        {/* KPI Stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {[
            {
              title: "Total Revenue",
              value: stats ? "$" + (stats.totalRevenue / 100).toFixed(2) : "—",
              icon: <DollarOutlined style={{ color: "#6366f1" }} />,
              color: "#6366f1",
            },
            {
              title: "This Month",
              value: stats ? "$" + (stats.thisMonthRevenue / 100).toFixed(2) : "—",
              icon: <RiseOutlined style={{ color: "#22c55e" }} />,
              color: "#22c55e",
              suffix: stats && stats.lastMonthRevenue > 0 ? (
                <span style={{ fontSize: 12, color: stats.thisMonthRevenue >= stats.lastMonthRevenue ? "#22c55e" : "#ef4444", marginLeft: 8 }}>
                  {stats.thisMonthRevenue >= stats.lastMonthRevenue ? "▲" : "▼"}{" "}
                  vs ${(stats.lastMonthRevenue / 100).toFixed(0)} last month
                </span>
              ) : null,
            },
            {
              title: "Active Subscribers",
              value: stats?.activeCount ?? "—",
              icon: <CheckCircleOutlined style={{ color: "#22c55e" }} />,
              color: "#22c55e",
            },
            {
              title: "Trial Users",
              value: stats?.trialCount ?? "—",
              icon: <ClockCircleOutlined style={{ color: "#3b82f6" }} />,
              color: "#3b82f6",
            },
            {
              title: "Past Due",
              value: stats?.pastDueCount ?? "—",
              icon: <ExclamationCircleOutlined style={{ color: "#f59e0b" }} />,
              color: "#f59e0b",
            },
            {
              title: "Total Companies",
              value: stats?.totalCompanies ?? "—",
              icon: <TeamOutlined style={{ color: "#8b5cf6" }} />,
              color: "#8b5cf6",
            },
          ].map(({ title, value, icon, color, suffix }) => (
            <Col xs={12} sm={8} md={4} key={title}>
              <Card size="small" loading={statsLoading} style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)", height: "100%" }}>
                <Statistic
                  title={<span style={{ fontSize: 12, color: "#6b7280" }}>{title}</span>}
                  value={value}
                  prefix={icon}
                  valueStyle={{ fontSize: 20, fontWeight: 700, color }}
                />
                {suffix}
              </Card>
            </Col>
          ))}
        </Row>

        <div className="card">
          <div className="card-body">
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
              <Input
                placeholder="Search companies…"
                style={{ maxWidth: 260 }}
                value={searchInput}
                onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
                allowClear
              />
              <Select
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(v) => { setStatusFilter(v); setPage(1); }}
                style={{ minWidth: 160 }}
              />
              <RangePicker
                onChange={(dates) => { setDateRange(dates); setPage(1); }}
                allowClear
              />
            </div>
            <Table
              columns={mainColumns}
              dataSource={companies}
              loading={loading}
              rowKey="_id"
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              onPaginationChange={(p, ps) => {
                setPage(p);
                setPageSize(ps);
              }}
            />
          </div>
        </div>

        <Modal
          title={modalTitle}
          open={invoiceModal}
          onCancel={() => {
            setInvoiceModal(false);
            setSelectedCompany(null);
            setInvoices([]);
          }}
          footer={null}
          width={900}
        >
          <Table
            columns={invoiceColumns}
            dataSource={invoices}
            loading={invoicesLoading}
            rowKey="stripeInvoiceId"
            showRowSelection={false}
            pagination={{
              current: invoicePage,
              pageSize: invoicePageSize,
              total: invoicesTotal,
              showSizeChanger: false,
            }}
            onPaginationChange={(p) => handleInvoicePageChange(p)}
          />
        </Modal>
      </div>
    </div>
  );
}
